import React from 'react';
import { Button, DialogDescription, DialogFooter } from '@ohif/ui-next';

const COPY = {
  completed: {
    title: 'Report Generated',
    description:
      'Report has been generated for this study. The page needs to be refreshed to show the results.',
  },
  failed: {
    title: 'Report Generation Failed',
    description:
      'Report generation has failed for this study. Please try again later or contact support if the problem persists.',
  },
};

export type DiagnosisCompleteVariant = keyof typeof COPY;

interface DiagnosisCompleteDialogProps {
  variant: DiagnosisCompleteVariant;
  /** Injected by ModalProvider alongside `show` (ModalProvider.tsx:89-93). Never passed by us. */
  hide?: () => void;
}

/**
 * The BODY only. Modal owns the Dialog / DialogContent / DialogHeader shell and
 * renders the title from the `title` option, so this component starts at the description.
 *
 * The component is develop's ReportGenerationFinished.tsx, which develop wrote against
 * @ohif/ui-next with these exact copy strings, never wired up, and then shadowed by
 * inlining an @ohif/ui Dialog into Mode.tsx instead. 3.13 deleted @ohif/ui's Dialog and
 * its contextProviders wholesale, so the abandoned file is the one that survives the
 * migration — and its home moves from platform/app into the extension.
 *
 * The copy is written as literals. i18n is parent §5.3's M5 work (§4.1).
 */
export function DiagnosisCompleteDialog({ variant, hide }: DiagnosisCompleteDialogProps) {
  const { description } = COPY[variant];

  return (
    <>
      {/* M5.5 D10.1. Fourth instance of the uncoloured DialogDescription -- see
          WorklistDelete/DeleteConfirmDialog.tsx for the mechanism. */}
      <DialogDescription className="text-white">{description}</DialogDescription>

      <DialogFooter>
        {variant === 'completed' ? (
          <>
            {/* M5.5 D1. develop's dismiss is variant="cancel", which declares no fill in either
                state, where ghost's hover:bg-primary/25 composites #543857 on this dialog's
                bg-muted shell -- the navy-backdrop plum. Replayed as parity, at the CALL SITE and
                not in brand.css: cn() is twMerge, so hover:bg-transparent DROPS the ghost class
                before it reaches the DOM and costs no CSS at all. The label dim is the other half
                of the idiom, and is not decoration -- without it, removing the fill leaves the
                button with no hover feedback. Same treatment as DeleteConfirmDialog.tsx's Cancel
                and UploadConfirmDialog.tsx's Stop, which is now all four dismiss buttons. */}
            <Button
              variant="ghost"
              className="hover:text-foreground/60 hover:bg-transparent"
              dataCY="sycai-refresh-later"
              onClick={() => hide?.()}
            >
              Refresh Later
            </Button>
            {/* M5.5 R-K: white on the orange accent, as a class -- see DropZone.tsx for why
                not --primary-foreground. */}
            <Button
              variant="default"
              className="text-white"
              dataCY="sycai-refresh-now"
              onClick={() => {
                hide?.();
                // D10. Parent §6 offers an event-driven refresh through
                // updateDataSourceConfiguration's unconditional broadcast; declined
                // here, because that path refreshes the STUDY LIST, whereas a completed
                // diagnosis means new SR/RT display sets mid-session. Invalidating those
                // without a reload is a larger change than this milestone's mandate.
                // Recorded as available and deliberately not taken.
                window.location.reload();
              }}
            >
              Refresh Now
            </Button>
          </>
        ) : (
          <Button
            variant="default"
            className="text-white"
            dataCY="sycai-diagnosis-failed-cancel"
            onClick={() => hide?.()}
          >
            Cancel
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

type DiagnosisCompleteModalOptions = {
  title: string;
  content: typeof DiagnosisCompleteDialog;
  contentProps: { variant: DiagnosisCompleteVariant };
  shouldCloseOnEsc: boolean;
  shouldCloseOnOverlayClick: boolean;
};

/**
 * D3. The service, not uiDialogService: this dialog is titled, overlaid, app-level and
 * raised from onModeEnter, which is the modal shape. In-tree precedent for raising one
 * from extension init code: extensions/cornerstone/src/init.tsx:417-421.
 *
 * It also gains a focus trap by moving, which D3's rationale does not claim: Modal.tsx:26-31
 * renders Dialog WITHOUT modal={false}, so Radix's default modal={true} applies — the
 * opposite of ManagedDialog.tsx:115, and the reason AssessmentDialog could not take this
 * route (D2).
 *
 * `hide` is not passed: ModalProvider injects it, along with `show`, into the content
 * component at :89-93.
 *
 * Every option is passed on every call. ModalProvider.show MERGES into the previous
 * options (`setOptions(prev => ({ ...prev, ...props }))`, :62), so a key omitted here
 * would silently retain the value from the previous raise — a failed run inheriting the
 * completed run's title.
 *
 * shouldCloseOnEsc and shouldCloseOnOverlayClick are set explicitly even though D4 wants
 * what they express: UIModalService.show defaults BOTH to true (UIModalService/index.ts:30-31),
 * so unlike the uiDialogService path these are corrections, not restatements of a default.
 */
export function showDiagnosisCompleteDialog(
  uiModalService: AppTypes.UIModalService,
  variant: DiagnosisCompleteVariant
): void {
  const options: DiagnosisCompleteModalOptions = {
    title: COPY[variant].title,
    content: DiagnosisCompleteDialog,
    contentProps: { variant },
    shouldCloseOnEsc: false,
    shouldCloseOnOverlayClick: false,
  };

  // show() destructures its options as `content = null, contentProps = null, title =
  // null`, annotating none of them (UIModalService/index.ts:25-33), so TypeScript infers
  // all three as `null | undefined` and rejects a real component, props object and title
  // alike. Upstream's own call site (init.tsx:420-423) passes the same shapes. It is not
  // checked under this config: extensions/sycai/tsconfig.json's own `include` REPLACES
  // rather than merges with the root tsconfig's, scoping the program to `src/**/*`, and
  // nothing under this extension imports @ohif/extension-cornerstone, so tsc never visits
  // init.tsx. `options` above is still checked against DiagnosisCompleteModalOptions;
  // only this handoff is not.
  uiModalService.show(options as unknown as Parameters<AppTypes.UIModalService['show']>[0]);
}
